<template>
    <div class="mb-3" :data-lc-field="fieldData.name" :data-invalid="visibleError ? 'true' : null">
        <!-- data-invalid marks a field showing an error, so a failed Next can scroll to the first one (utils/scroll.js) -->
        <!-- showing this when the step is 'readonly' and presented as summarized info -->
        <div v-if="isReadOnlyView" data-readonly>
            <div v-if="!fieldData.hasOwnProperty('hideWhenReadOnly') || !fieldData.hideWhenReadOnly">
                <span v-show="!!fieldData.label">&#x2022; {{ fieldLabel }}</span>
                <p v-if="app[fieldData.name]" class="text-muted" v-bind="fieldData.attrs">{{app[fieldData.name] ?? ''}}</p>
                <p v-else class="text-danger"><i>{{ getTranslatedText('__answer_not_provided__', 'fields.labels') }}</i></p>
            </div>
            <LcFieldErrors v-if="visibleError" :id="errorId" :errors="[visibleError]" />
        </div>
        <!-- regular input group -->
        <div v-else>
            <!-- a field with an `info` note gets an ⓘ after its label; the bubble opens on a line of its own under the label -->
            <div v-if="infoText" class="info-tip-row d-flex flex-wrap align-items-center gap-1 mb-2">
                <label v-show="!!fieldData.label && fieldData.label.length > 0" :for="fieldData.name" class="form-label mb-0" v-html="fieldLabel"></label>
                <InfoTip :id="infoId" :text="infoText" :label="getTranslatedText('__more_info__', 'buttons') ?? undefined" />
            </div>
            <label v-else v-show="!!fieldData.label && fieldData.label.length > 0" :for="fieldData.name" class="form-label" :class="{ 'mb-1': !!helpText }" v-html="fieldLabel"></label>
            <!-- a field with a `help` note shows it under its label: read before typing, and not hidden by a phone keyboard -->
            <div v-if="helpText" :id="helpId" class="form-text mt-0 mb-2">{{ helpText }}</div>
            <div class="mb-3" :class="inputGroupClass" @keydown.enter="onEnterKey">
                <span v-if="!!fieldData.prependInputSpan" class="input-group-text">{{fieldData.prependInputSpan}}</span>
                <component :is="getFieldComponent(fieldData.type)"
                    :showAs="fieldData.showAs"
                    :id="fieldData.name"
                    :name="fieldData.name"
                    :addressLabel="getTranslatedText(fieldData.addressLabel, 'fields.labels') ?? ''"
                    :type="fieldData.type ?? 'text'"
                    :fieldInnerClass="fieldData.fieldInnerClass ?? null"
                    v-bind="inputAttrs"
                    v-model="app[fieldData.name]"
                    :value="shownValue"
                    :placeholder="getTranslatedText(fieldData.placeholder, 'fields.placeholders') ?? ''"
                    :choices="getDynamicChoices(fieldData.choices ?? null)"
                    :isMultipleOption="fieldData.isMultipleOption ?? false"
                    :lang="!!config.lang && config.lang === 'fr' ? 'fr' : 'en'"
                    :class="{ 'is-invalid': !!visibleError }"
                    :aria-invalid="visibleError ? 'true' : null"
                    :aria-describedby="describedBy"
                    @input="onFieldInput"
                    @inputFromRadio="onFieldPick"
                    @inputFromRadioButton="handleInputFromRadioButton($event)"
                    @goToNextStep="$emit('goToNextStep');"
                    @validateAddress="validateAddressField"
                    @blur="onFieldBlur"
                    @blurFromDate="onFieldPick"
                    :fieldValidator="fieldValidator"
                    :inputColor="inputColor"
                    :isStepReadOnly="!!isStepReadOnly && (!fieldData.hasOwnProperty('allowReadOnly') || !!fieldData.allowReadOnly)"
                    :hideSubtitles="!!fieldData.hideSubtitles"
                    :prepopAddress="app['prepop_address'] ?? null"
                    :addressProp="app['address'] ?? null"
                    :productName="app['product_name'] ?? null"
                    ></component>
                <span v-if="!!fieldData.appendInputSpan" class="input-group-text">{{fieldData.appendInputSpan}}</span>
                <LcFieldErrors v-if="fieldMessage" :id="errorId" :errors="[fieldMessage]" />
            </div>
        </div>
    </div>
</template>

<script setup>

import * as fieldHelper from "@/form/helpers/field-types.js";
import { inject, ref, computed, onBeforeUnmount } from 'vue';
import { fieldValidationHelper, addressFieldValidationHelper, validateEmailWithBackend } from "@/form/helpers/field-validations.js";
import { TRIGGERS, EMAIL_UNDELIVERABLE_MESSAGE, visibleErrors, isEmptyValue, messageForFieldType } from "@/form/helpers/validation-display.js";
import { keyboardHintsFor } from "@/form/helpers/input-hints.js";
import { inputFormatFor } from "@/form/helpers/input-formats.js";
import LcFieldErrors from '@/components/form/LcFieldErrors.vue';
import InfoTip from '@/components/form/InfoTip.vue';
import debounce from 'lodash.debounce';

const props = defineProps({
    fieldData: {
        type: Object,
        required: true,
    },
    shouldValidate: {
        type: Boolean,
        default: true
    },
    fieldValidator: {
        type: Object,
        default: () => ({})
    },
    inputColor: {
        type: Object,
        default: () => ({})
    },
    isStepReadOnly: {
        type: Boolean,
        default: false
    },
    isStepEditable: {
        type: Boolean,
        default: true
    },
});

const emit = defineEmits(['goToNextStep', 'enter']);

const { app, config, updateApplication, updateApplicationField, getTranslatedText } = inject('app');

// What is in the box while the person types, for a field that holds something else: a rate typed
// as 6.99 is held (and sent) as 7 from the first keystroke, $321.50 as 321, but the box keeps what
// was typed until the person leaves it.
const typedText = ref(null);

// --- Methods ---

// `trigger` is the moment that caused the update (see validation-display.js); without one the
// value is only synced, not validated.
function updateFieldValue(value, trigger = null) {
    const trimmedValue = (typeof value === 'string') ? value.trim() : value;
    props.fieldData.value = trimmedValue;
    updateApplicationField(props.fieldData.name, trimmedValue);
    if (trigger) {
        validateField(trimmedValue, trigger);
    } else {
        // Keep the validator's value in sync even when not validating (typing before the
        // debounce fires, radio auto-advance). validateForm() runs unconditionally during
        // step advance / submit; with a stale value it would flag the field as
        // "__required_field__" — visible on the last step, which stays mounted while the
        // submit request is in flight.
        props.fieldValidator.value = trimmedValue;
    }
}

function markDirty() {
    props.fieldValidator.isDirty = true;
}

// The value the field holds for what is in the box (input-formats.js): "6.99" -> "7" for a rate,
// "321.50" -> "321" for a number, the same text for everything else.
function heldValue(text) {
    const toValue = inputFormat.value?.toValue;
    return toValue ? toValue(text) : text;
}

// Typing: sync now, re-check after the person pauses. The re-check can only update or clear an
// error that is already showing, never add one.
function onFieldInput(value) {
    markDirty();
    if (inputFormat.value?.toValue) {
        typedText.value = value;
    }
    const held = heldValue(value);
    updateFieldValue(held);
    debounceHandler(held);
}

// Native inputs pass the blur event; vue-datepicker emits `blur` with no payload, so fall back
// to the value the field already holds. Leaving the box shows what the field holds (6.99 -> 7,
// 321.50 -> 321).
function onFieldBlur(event) {
    const target = event?.target;
    const value = (target && 'value' in target) ? heldValue(target.value) : props.fieldValidator?.value;
    typedText.value = null;
    debounceHandler.cancel();
    updateFieldValue(value, TRIGGERS.BLUR);
}

// A discrete pick (radio list, date) is a finished answer, so it is checked right away.
function onFieldPick(value) {
    markDirty();
    debounceHandler.cancel();
    updateFieldValue(value, TRIGGERS.PICK);
}

function handleInputFromRadioButton(value) {
    markDirty();
    updateFieldValue(value);
    emit('goToNextStep', true);
}

// Enter in a text box moves the form on like Next (LcForm decides what that means on the step).
// Text fields only: elsewhere Enter has its own job (a calendar date, an address suggestion).
function onEnterKey(event) {
    if (!isTextField.value || event.isComposing || event.defaultPrevented) {
        return;
    }
    event.preventDefault();
    emit('enter');
}

function getFieldComponent(fieldType) {
    return fieldHelper.getComponent(fieldType);
}

function validateField(value, trigger) {
    const validator = props.fieldValidator;
    validator.value = value;
    const result = fieldValidationHelper(validator, app.value, config.value.country);
    validator.isValid = result.isValid;
    validator.errorMessage = visibleErrors({
        trigger,
        value,
        result,
        isDirty: !!validator.isDirty,
        revealed: !!validator.revealed,
        shown: validator.errorMessage,
    });

    // The deliverability check is a network call: run it when the person leaves a field they
    // changed, not on every typing pause.
    if (result.isValid && trigger === TRIGGERS.BLUR && validator.isDirty && !isEmptyValue(value)) {
        const emailFieldsToValidate = ['email'];
        if (emailFieldsToValidate.includes(props.fieldData.name)) {
            validateEmailWithLC(value);
        }
    }
}

function validateAddressField(addressValue) {
    props.fieldValidator.value = addressValue;
    const validatorHelper = addressFieldValidationHelper(addressValue, config.value.country);
    props.fieldValidator.isValid = validatorHelper.isValid;
    props.fieldValidator.invalidAddressFields = validatorHelper.invalidFields ?? [];
    // clear early: a completed address drops the message Next put up (and its data-invalid mark)
    if (validatorHelper.isValid) {
        props.fieldValidator.errorMessage = [];
    }
}

async function validateEmailWithLC(value) {
    const validator = props.fieldValidator;
    const result = await validateEmailWithBackend(value);
    // The person may have kept typing while the check was in flight; a verdict on an older
    // value must not overwrite the state of the newer one.
    if (validator.value !== value) {
        return;
    }
    if (result.isValid) {
        validator.isValid = true;
        validator.errorMessage = [];
    } else {
        validator.isValid = false;
        validator.errorMessage = [EMAIL_UNDELIVERABLE_MESSAGE];
    }
}

function getDynamicChoices(choices = null) {
    if (!!choices && choices.length > 0) {
        return typeof choices === 'function' ? choices({ ...app.value }) : choices;
    }
    return false;
}

// --- Computed ---

const isReadOnlyView = computed(() => {
    return !!props.isStepReadOnly
        && (!props.fieldData.hasOwnProperty('allowReadOnly') || !!props.fieldData.allowReadOnly)
        && props.fieldData.type !== 'fulladdress';
});

// The one message on screen for this field, if any. `errorMessage` holds only what the display
// rules allow (validation-display.js); `isValid` stays the truth used to gate Next.
const visibleError = computed(() => {
    if (!props.shouldValidate) {
        return null;
    }
    const messages = props.fieldValidator?.errorMessage;
    if (!Array.isArray(messages) || messages.length === 0) {
        return null;
    }
    return messageForFieldType(messages[0], props.fieldData.type);
});

// The line rendered under the control. The address shows its own messages (GoogleMaps.vue):
// "Enter your address" under the search box, or one per part once the six fields are open.
const fieldMessage = computed(() => props.fieldData.type === 'fulladdress' ? null : visibleError.value);

const errorId = computed(() => `${props.fieldData.name}-error`);

// Optional note behind an ⓘ next to the label: `info: '__key__'` in a schema, from fields.info.
const infoText = computed(() => props.fieldData.info ? getTranslatedText(props.fieldData.info, 'fields.info') : null);
const infoId = computed(() => `${props.fieldData.name}-info`);

// Optional note shown under the label: `help: '__key__'` in a schema, from fields.help.
const helpText = computed(() => props.fieldData.help ? getTranslatedText(props.fieldData.help, 'fields.help') : null);
const helpId = computed(() => `${props.fieldData.name}-help`);

const describedBy = computed(() => [
    fieldMessage.value ? errorId.value : null,
    helpText.value ? helpId.value : null,
    infoText.value ? infoId.value : null,
].filter(Boolean).join(' ') || null);

const isTextField = computed(() => !props.fieldData.type || props.fieldData.type === 'text');

// How a text field cleans what is typed, from its validation rules (input-formats.js).
const inputFormat = computed(() => isTextField.value
    ? inputFormatFor(props.fieldData.validationRules, props.fieldData.attrs?.maxlength)
    : null);

// The right on-screen keyboard for text fields, from their validation rules (input-hints.js).
// The schema's own attrs come last, so anything a field sets explicitly still wins, except the
// maxlength of a field that cleans what is typed (input-formats.js): "(514) 111-5555" needs more
// than 10 characters on the way to 10 digits, so the format takes over that limit.
const inputAttrs = computed(() => {
    if (!isTextField.value) {
        return { ...(props.fieldData.attrs ?? {}) };
    }
    const attrs = { ...keyboardHintsFor(props.fieldData.validationRules), ...(props.fieldData.attrs ?? {}) };
    const format = inputFormat.value;
    if (format) {
        attrs.normalize = format.normalize;
        attrs.maxlength = format.maxlength;
    }
    return attrs;
});

// What the box shows: what the field holds, or the text being typed while it still stands for
// that value. Re-rendering the box with "7" would otherwise wipe the "6.9" someone is typing.
const shownValue = computed(() => {
    const held = app.value[props.fieldData.name] ?? null;
    if (typedText.value !== null && heldValue(typedText.value) === held) {
        return typedText.value;
    }
    return held;
});

// A prefix ($) or a suffix (.00, %) turns the wrapper into a Bootstrap input group; a suffix on
// its own needs it too, or the % would drop onto its own line.
const hasInputGroup = computed(() => !!props.fieldData.prependInputSpan || !!props.fieldData.appendInputSpan);

// `has-validation` tells Bootstrap the group's last child is the error message, so it rounds the
// element before it instead. Only add it while the message is actually rendered: with no error
// it would round the input's corners where it meets a suffix (%, .00) and they wouldn't sit flush.
const inputGroupClass = computed(() => {
    if (!hasInputGroup.value) {
        return '';
    }
    return fieldMessage.value ? 'input-group has-validation' : 'input-group';
});

const fieldLabel = computed(() => {
    const labelKey = (typeof props.fieldData.label === 'function') ? props.fieldData.label({ ...app.value }) : props.fieldData.label;
    const translatedValue = getTranslatedText(labelKey, 'fields.labels') ?? '';
    return translatedValue;
});

// --- Debounce ---

const debounceHandler = debounce(value => {
    updateFieldValue(value, TRIGGERS.INPUT);
}, 500);

onBeforeUnmount(() => debounceHandler.cancel());

</script>
